package com.example.borshi.web.model

/**
 * User-set targets for DIY mix builder.
 * Drives progress bars and match scoring.
 * Includes micronutrient / health focus toggles for personalized recommendations.
 */
data class UserTargets(
    val goalPreset: PlanCategory = PlanCategory.GENERAL_HEALTH,
    val proteinPerDayGrams: Int = 100,
    val budgetPerWeekRupees: Double = 0.0, // 0 = no ceiling
    val omega3PerWeekMg: Int = 0, // 0 = no target; e.g. 2000–3500 for heart/brain
    // Health focus toggles — boost match score for products rich in these
    val maximizeOmega3: Boolean = false,
    val prioritizeB12: Boolean = false,
    val prioritizeVitaminD: Boolean = false,
    val prioritizeZinc: Boolean = false,
    val prioritizeSelenium: Boolean = false,
    val prioritizeIodine: Boolean = false,
    val prioritizeMagnesium: Boolean = false,
    val lowMercuryPregnancy: Boolean = false,
    val lowCarbDiabetes: Boolean = false
) {
    /** Default protein by goal (weekly est. × 7 days simplified) */
    fun defaultProteinForGoal(): Int = when (goalPreset) {
        PlanCategory.MUSCLE_BUILDING -> 150
        PlanCategory.ATHLETE -> 160
        PlanCategory.HEART_HEALTH -> 100
        PlanCategory.PREGNANCY -> 80
        PlanCategory.WEIGHT_LOSS -> 120
        PlanCategory.SENIOR -> 90
        PlanCategory.DIABETES -> 100
        PlanCategory.GENERAL_HEALTH -> 100
    }

    /** Whether user has set a budget ceiling */
    fun hasBudgetCeiling(): Boolean = budgetPerWeekRupees > 0

    /** Whether user has set an omega-3 target */
    fun hasOmega3Target(): Boolean = omega3PerWeekMg > 0

    /** Default omega-3 (mg/week) by goal when maximizeOmega3 is on */
    fun defaultOmega3ForGoal(): Int = when (goalPreset) {
        PlanCategory.HEART_HEALTH, PlanCategory.PREGNANCY -> 2500
        PlanCategory.SENIOR, PlanCategory.GENERAL_HEALTH -> 2000
        else -> 1500
    }

    /** Any health focus toggled */
    fun hasHealthFocus(): Boolean = maximizeOmega3 || prioritizeB12 || prioritizeVitaminD ||
        prioritizeZinc || prioritizeSelenium || prioritizeIodine || prioritizeMagnesium ||
        lowMercuryPregnancy || lowCarbDiabetes
}
